import { useMemo, useRef, useState } from 'react';
import type { ApiReconstructedDocumentListItem } from '@avisoma/shared';
import { useInfiniteList } from '../../hooks/useInfiniteList';
import backend from '../../services/backend';
import type { ReconstructedDocumentsFilters } from '../../components/ReconstructedDocumentsFilters';

const INITIAL_ITEMS_PER_PAGE = 50;

export function useReconstructedDocumentsList() {
  const [filters, setFilters] = useState<ReconstructedDocumentsFilters>();
  const [totalCount, setTotalCount] = useState(0);

  // Track the latest filters identity so we can ignore out-of-order queryFn
  // resolutions when updating totalCount (an older request finishing after a
  // newer one mustn't overwrite the count with a stale value).
  const latestFiltersRef = useRef(filters);
  latestFiltersRef.current = filters;

  const queryKey = useMemo(() => ['reconstructed-documents', filters], [filters]);

  const result = useInfiniteList<ApiReconstructedDocumentListItem>({
    queryKey,
    queryFn: async (limit) => {
      const requestFilters = filters;
      const res = await backend.listReconstructedDocuments({
        search: filters?.search || undefined,
        classification: filters?.classification ?? undefined,
        tags: filters?.tags?.length ? filters.tags : undefined,
        hasExtraction:
          filters?.hasExtraction === 'yes' || filters?.hasExtraction === 'no'
            ? filters.hasExtraction
            : undefined,
        limit: String(limit),
      });
      if (latestFiltersRef.current === requestFilters) {
        setTotalCount(res.data.totalCount);
      }
      return { items: res.data.items, hasMore: res.data.hasMore };
    },
    enabled: !!filters,
    initialItemsPerPage: INITIAL_ITEMS_PER_PAGE,
    resetKey: filters,
  });

  return {
    ...result,
    totalCount,
    setFilters,
  };
}
